import { useEffect, useState } from 'react'
import getContrast from 'helpers/color/getContrast'

type HookProps = {
  imageUrl: string | null | undefined
  darkColor?: string
  lightColor?: string
}

const useImageTextColor = ({
  imageUrl,
  lightColor = 'text-white',
  darkColor = 'text-slate-900',
}: HookProps) => {
  const [textColorClass, setTextColorClass] = useState(darkColor)

  useEffect(() => {
    if (!imageUrl) {
      setTextColorClass(darkColor)
      return
    }

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const img = new Image()
    img.crossOrigin = 'anonymous'

    img.onload = () => {
      const maxSize = 100
      const scale = Math.min(maxSize / img.width, maxSize / img.height)
      canvas.width = img.width * scale
      canvas.height = img.height * scale

      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
      const data = imageData.data

      let r = 0,
        g = 0,
        b = 0,
        count = 0

      for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] > 128) {
          r += data[i]
          g += data[i + 1]
          b += data[i + 2]
          count++
        }
      }

      if (count > 0) {
        r = Math.round(r / count)
        g = Math.round(g / count)
        b = Math.round(b / count)

        const hex = `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`

        const contrastWithWhite = getContrast(hex, '#ffffff')
        const contrastWithBlack = getContrast(hex, '#000000')

        const textColor =
          contrastWithWhite > contrastWithBlack ? lightColor : darkColor
        setTextColorClass(textColor)
      } else {
        setTextColorClass(darkColor)
      }
    }

    img.onerror = () => {
      setTextColorClass(darkColor)
    }

    img.src = imageUrl
  }, [imageUrl])

  return textColorClass
}

export default useImageTextColor
